// ─── Vue ANNÉE : 12 mini-mois (2 colonnes), appui = ouvrir le mois ──
// Extrait d'EcranCalendrier (découpage) — même sémantique que la vue Mois,
// en miniature : fond = GARDE (pastel), anneau = ÉVÉNEMENT(S), ligne dessous
// = férié (rouge) ou vacances (teal). Le scroll se cale sur le mois courant
// à l'entrée (une fois par montage — le composant est démonté en quittant).
import React, { useMemo, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT } from '@mop/core';
import { dateKey } from './services/evenements';
import { estMasqueEvenement } from './data/categories';

const premierJourCol = (a, m) => (new Date(a, m, 1).getDay() + 6) % 7;   // 0 = lundi
const nbJours = (a, m) => new Date(a, m + 1, 0).getDate();
// Numéro de semaine ISO 8601 (lundi = 1er jour, la semaine du 1er jeudi = S1).
const numSemaine = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7) + 3);
  const jeudi1 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((date - jeudi1) / 86400000 - 3 + ((jeudi1.getUTCDay() + 6) % 7)) / 7);
};

export default function VueAnnee({
  an, map, garde, gardeCalculee, montrerGarde, vacVisible, estFerie, estVacances,
  catMasquees, gardeCol, colFerie, colVac, scrollPage, onOuvrirMois,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const MOIS = t('commun.moisLongs');
  const auj = new Date();
  const anGrilleY = useRef(0);
  const anScrollFait = useRef(false);

  return (
    <View style={s.anGrille} onLayout={(e) => { anGrilleY.current = e.nativeEvent.layout.y; }}>
      {Array.from({ length: 12 }, (_, m) => {
        const prem = premierJourCol(an, m), nb = nbJours(an, m);
        const estAujMois = an === auj.getFullYear() && m === auj.getMonth();
        return (
          <TouchableOpacity key={m} style={s.anMois} activeOpacity={0.7}
            onLayout={estAujMois ? (e) => {
              // Positionne le scroll pour montrer le mois COURANT au milieu
              // (une seule fois par entrée dans la vue Année).
              if (anScrollFait.current) return;
              anScrollFait.current = true;
              scrollPage?.current?.scrollTo({ y: Math.max(0, anGrilleY.current + e.nativeEvent.layout.y - 120), animated: false });
            } : undefined}
            onPress={() => onOuvrirMois?.(m, estAujMois ? auj.getDate() : 1)}>
            <Text style={[s.anMoisTitre, estAujMois && { color: UI.accent }]}>{MOIS[m]}</Text>
            {(() => {
              // Rangées semaine par semaine : n° ISO à gauche, 7 cellules.
              const cellules = [
                ...Array.from({ length: prem }, () => null),
                ...Array.from({ length: nb }, (_, i) => i + 1),
              ];
              while (cellules.length % 7) cellules.push(null);
              const semaines = [];
              for (let i = 0; i < cellules.length; i += 7) semaines.push(cellules.slice(i, i + 7));
              while (semaines.length < 6) semaines.push(Array.from({ length: 7 }, () => null));   // même hauteur pour tous les mois
              return semaines.map((sem, si) => {
                const dRef = sem.find(x => x != null);
                const ns = dRef ? numSemaine(new Date(an, m, dRef)) : '';
                return (
                  <View key={si} style={s.anSem}>
                    <Text style={s.anSemNum}>{ns}</Text>
                    {sem.map((d, ci) => {
                      if (!d) return <View key={ci} style={s.anJour} />;
                      const dk = dateKey(an, m, d);
                      const estAuj = estAujMois && d === auj.getDate();
                      const g = montrerGarde ? (garde[dk] || gardeCalculee(dk)) : null;
                      const fer = estFerie(dk);
                      const vac = vacVisible && estVacances(dk);
                      const aEvt = (map[dk] || []).some(ev => !estMasqueEvenement(ev, catMasquees));
                      return (
                        <View key={ci} style={s.anJour}>
                          <View style={[s.anRond, g && { backgroundColor: (gardeCol[g] || UI.accent) + '38' }, estAuj && s.anJourAuj, aEvt && s.anAnneau]}>
                            <Text style={[s.anJourTxt, g && !estAuj && { color: gardeCol[g] || UI.accent, fontWeight: '800' }, estAuj && s.anJourTxtAuj]}>{d}</Text>
                          </View>
                          {(fer || vac) && <View style={[s.anLigne, { backgroundColor: fer ? colFerie : colVac }]} />}
                        </View>
                      );
                    })}
                  </View>
                );
              });
            })()}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  anGrille: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  anMois: { flexBasis: '47%', flexGrow: 1, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 8 },
  anMoisTitre: { fontSize: 13, fontWeight: '800', color: UI.ink, marginBottom: 4, marginLeft: 2 },
  anSem: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
  anSemNum: { width: 15, fontSize: 8, color: UI.faint, fontWeight: '700' },
  anJour: { flex: 1, height: 23, alignItems: 'center', justifyContent: 'flex-start' },
  anRond: { width: 17, height: 17, borderRadius: 999, alignItems: 'center', justifyContent: 'center' },
  anJourAuj: { backgroundColor: UI.accent },
  anAnneau: { borderWidth: 1.1, borderColor: UI.accent },
  anJourTxt: { fontSize: 9.5, color: UI.muted },
  anJourTxtAuj: { color: '#fff', fontWeight: '800' },
  // Pleine largeur de cellule → la ligne est CONTINUE d'un jour à l'autre.
  anLigne: { position: 'absolute', bottom: 0, width: '100%', height: 2 },
});
